import { useEffect, useState } from "react";
import { Switch } from "@fluentui/react-components";
import { SearchTextBox } from "../Primitives/TextBox";
import type { TMatter } from "../../models/matter-model";
import { SearchPresentational, ViewType } from "./SearchPresentational";
import { useNotificationStore } from "../Notifications/notification-store";
import type { TMatterRetriever, TSearchFlags } from "../../services/matter-retriever";
import type { Props as MatterPresentationalProps } from "../Matter/MatterPresentational";
import { TSmokeballTier } from "../../services/smokeball-authenticator";

export type Props = {
  tier: TSmokeballTier;
  matterRetriever: TMatterRetriever;
  onMatterSelected: MatterPresentationalProps["onMatterSelected"];
};

export const SearchContainer = (props: Props) => {
  const minSearchLength = 2;
  const searchErrorTitle = "Could not load search results";
  const recentMattersErrorTitle = "Could not load recent matters";

  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [matters, setMatters] = useState<TMatter[]>([]);
  const [viewType, setViewType] = useState<ViewType>("DEFAULT"); //TODO need to access this from onMatterOpened...?
  const [includeClosedMatters, setIncludeClosedMatters] = useState(false);
  const errorNotification = useNotificationStore((s) => s.errorNotification);

  useEffect(() => {
    getMattersAsync("", false).catch((error) => errorNotification(recentMattersErrorTitle, error));
    setViewType("RECENT");
  }, []);

  const onIncudeClosedMattersChanged = (ev: { currentTarget: { checked: boolean } }) => {
    const isChecked = ev.currentTarget.checked;

    setIncludeClosedMatters(isChecked);
    getMattersAsync(searchText, isChecked).catch((error) => errorNotification(searchErrorTitle, error));
  };

  const getRecentMattersAsync = async () => {
    const result = await props.matterRetriever.myRecentMatters();

    if (!result.hasError) {
      return result.matters.length ? result.matters : [];
    } else {
      console.error(result.error);
      errorNotification(recentMattersErrorTitle, result.error);
    }

    return [];
  };

  const searchMattersAsync = async (searchText: string, includeClosedMatters: boolean) => {
    if (!searchText) {
      return [];
    }

    const searchFlags: TSearchFlags[] = includeClosedMatters ? ["INCLUDE_CLOSED"] : [];

    const result = await props.matterRetriever.search(searchText, searchFlags);

    if (!result.hasError) {
      return result.matters.length ? result.matters : [];
    } else {
      console.error(result.error);
      errorNotification(searchErrorTitle, result.error);
    }

    return [];
  };

  const getMattersAsync = async (searchText: string, includeClosed: boolean): Promise<void> => {
    setIsLoading(true);

    let matters = [] as TMatter[];

    if (searchText.length > 0) {
      try {
        matters = await searchMattersAsync(searchText, includeClosed);
      } catch (error) {
        errorNotification(searchErrorTitle, error);
      }
    } else {
      try {
        matters = await getRecentMattersAsync();
      } catch (error) {
        errorNotification(recentMattersErrorTitle, error);
      }
    }

    setSearchText(searchText);

    setViewType((prevViewType) => {
      return searchText.length > minSearchLength
        ? "SEARCH"
        : prevViewType === "SEARCH" && searchText.length === 0
        ? "RECENT"
        : prevViewType;
    });

    setMatters(matters);
    setIsLoading(false);

    if (!hasLoaded) {
      setHasLoaded(true);
    }
  };

  return (
    <SearchPresentational
      viewType={viewType}
      hasLoaded={hasLoaded}
      tier={props.tier}
      onMatterSelected={props.onMatterSelected}
      matters={viewType !== "DEFAULT" ? matters : null}
      resultsTitle={viewType === "SEARCH" ? "Search Results" : "Recent Matters"}
      searchTextBox={
        <>
          <SearchTextBox
            throttleMs={600}
            isLoading={isLoading}
            minLength={minSearchLength}
            onSearchAsync={(text) => getMattersAsync(text, includeClosedMatters)}
          />
          {viewType === "SEARCH" && (
            <Switch
              labelPosition="after"
              className="-ml-1 mt-1"
              checked={includeClosedMatters}
              onChange={onIncudeClosedMattersChanged}
              label={includeClosedMatters ? "Hide Closed Matters" : "Show Closed Matters"}
            />
          )}
        </>
      }
    />
  );
};
